import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import palette from '@ui/palette';

export interface RadioButtonProps {
  id: string;
  label: string;
  value: number;
  checked: boolean;
  onChange: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export type BaseRadioButtonProps = Pick<
  RadioButtonProps,
  'id' | 'label' | 'value'
>;

const useStyles = makeStyles()(theme => ({
  labelVisuallyHidden: {
    border: 0,
    clip: `rect(0 0 0 0)`,
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: 1,
  },
  container: {
    position: 'relative',
    borderRadius: theme.spacing(0.5),
    '& > input': {
      position: 'absolute',
      opacity: 0,
      cursor: 'pointer',
      height: '100%',
      width: '100%',
      margin: 0,
      '&[disabled]': {
        cursor: 'not-allowed',
      },
    },
    '&:has( > input:checked)': {
      // apply a margin of 1px to offset the outline
      marginLeft: `1px`,
      marginRight: `1px`,
      outline: `1px solid ${palette.mint_300}`,
      '@supports ( -moz-appearance:none )': {
        display: 'inherit',
      },
    },
    '&:has( > input:focus-visible)': {
      outline: `2px solid ${palette.mint_200}`,
    },
  },
}));

/**
 * Base component for custom radio buttons
 * - Removes the default radio button styling in favor of custom children passed in
 * - Appears as a button but implements label + input required for radio input
 */
const RadioButton = (props: RadioButtonProps) => {
  const {
    id,
    label,
    value,
    checked,
    onChange,
    onKeyDown,
    children,
    className,
    disabled,
  } = props;
  const { classes, cx } = useStyles();
  const [hover, setHover] = React.useState(false);

  const noop = () => {};
  const handleHover = (isHovered: boolean) => setHover(isHovered);

  return (
    <Box className={cx(className, classes.container)} data-hover={hover}>
      <label htmlFor={id} className={classes.labelVisuallyHidden}>
        {label}
      </label>
      <input
        data-e2e={`radio-input-${id}`}
        type="radio"
        id={id}
        disabled={disabled}
        name={id}
        value={value}
        aria-checked={checked}
        checked={checked}
        tabIndex={checked ? 0 : -1}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onMouseOver={() => handleHover(true)}
        onMouseOut={() => handleHover(false)}
        // To satisfy linter error - if mouse event exists, needs keyboard events
        // But the above is to solely handle hover so focus/blur not applicable
        onFocus={noop}
        onBlur={noop}
      />
      {children}
    </Box>
  );
};

export default RadioButton;
